/**
 * HashiCorp Configuration Language (HCL / Terraform) grammar for lowlight.
 *
 * highlight.js core does not ship an HCL grammar and the community
 * `highlightjs-terraform` package is not in our registry, so we register this
 * self-contained definition instead. It is a plain highlight.js language
 * definition (adapted from the MIT-licensed `highlightjs-terraform` grammar)
 * covering comments, strings with `${…}` interpolation, heredocs, numbers,
 * booleans, block keywords, and the common Terraform built-ins.
 *
 * Registered under "hcl" with "terraform"/"tf" aliases so jun0 code fences
 * (e.g. ```hcl) highlight instead of crashing CodeBlock.
 */

import type { LanguageFn } from "lowlight";

/**
 * HCL/Terraform grammar as a highlight.js `LanguageFn`. lowlight calls this with
 * its `hljs` instance and expects a language definition back; we ignore `hljs`
 * and return a self-contained definition. Typing it as `LanguageFn` lets it
 * register without a cast — the returned object is checked against `Language`.
 */
export const hcl: LanguageFn = () => {
  const INTERPOLATION = {
    className: "subst",
    begin: "\\$\\{",
    end: "\\}",
    keywords: {
      built_in: "var local module data path each count self terraform",
    },
  };

  const STRING = {
    className: "string",
    begin: '"',
    end: '"',
    contains: [INTERPOLATION],
  };

  return {
    keywords: {
      keyword:
        "resource variable provider output locals module data terraform " +
        "for for_each count depends_on lifecycle dynamic if in",
      literal: "true false null",
    },
    contains: [
      { className: "comment", begin: "#", end: "$" },
      { className: "comment", begin: "//", end: "$" },
      { className: "comment", begin: "/\\*", end: "\\*/" },
      // Heredoc strings: <<EOT … EOT and indented <<-EOT … EOT
      { className: "string", begin: "<<[-]?\\s*\\w+", end: "^\\s*\\w+\\s*$" },
      STRING,
      { className: "number", begin: "\\b\\d+(\\.\\d+)?", relevance: 0 },
      {
        className: "built_in",
        begin: "\\b(var|local|module|data|path|each|count|self)\\b",
      },
    ],
  };
};
